import { useState, useEffect } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { FinancialOverview } from '@/components/finances/FinancialOverview';
import { EarningsBreakdown } from '@/components/finances/EarningsBreakdown';
import { getClientsAnalytics, getClientAnalytics } from '@/services/analytics.service';
import { clientsService } from '@/services/clients.service';
import { useAuth } from '@/lib/auth-context';

function FinancesContent() {
  const [selectedPeriod] = useState('month');
  const [analytics, setAnalytics] = useState<any>(null);
  const [clientAnalytics, setClientAnalytics] = useState<any[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    if (!user) return;

    try {
      const token = await user.getIdToken();

      // Fetch aggregate analytics and all clients
      const [aggregateAnalytics, clients] = await Promise.all([
        getClientsAnalytics(token),
        clientsService.getClients(),
      ]);

      setAnalytics(aggregateAnalytics);

      // Fetch analytics for each client
      const clientAnalyticsPromises = clients.map(async (clientItem) => {
        try {
          const clientData = await getClientAnalytics(clientItem.id, token);
          return {
            ...clientData,
            clientName: clientItem.name,
          };
        } catch (err) {
          // If client has no analytics yet, return zeros
          return {
            clientId: clientItem.id,
            clientName: clientItem.name,
            totalEarnings: 0,
            totalHours: 0,
            activeTasks: 0,
            completedTasks: 0,
            totalInvoices: 0,
            paidInvoices: 0,
            unpaidAmount: 0,
          };
        }
      });

      const clientsData = await Promise.all(clientAnalyticsPromises);
      const clientsWithEarnings = clientsData.filter(c => c.totalEarnings > 0);
      setClientAnalytics(clientsWithEarnings);
    } catch (err: any) {
      console.error('Error loading analytics:', err);
    }
  };

  const totalEarnings = analytics?.totalEarnings ?? 0;

  // Map client analytics to earnings breakdown format
  const earningsByClient = clientAnalytics.map((client: any) => ({
    client: client.clientName,
    earnings: Math.round(client.totalEarnings),
    hours: parseFloat(client.totalHours?.toFixed(1) ?? '0'),
    hourlyRate: client.totalHours > 0 ? Math.round(client.totalEarnings / client.totalHours) : 0,
  }));

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Finances' }]} />

      {/* Financial Overview */}
      <FinancialOverview
        totalEarnings={totalEarnings}
        period={selectedPeriod}
      />

      {/* Earnings Breakdown */}
      <div className="mt-6">
        <EarningsBreakdown data={earningsByClient} />
      </div>
    </AppLayout>
  );
}

export default function FinancesPage() {
  return (
    <ProtectedRoute>
      <FinancesContent />
    </ProtectedRoute>
  );
}
